import { CheckCircle2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Ticket } from "../lib/types";

export function isTicketSubmissionNotice(state: unknown, ticketId: string): boolean {
  return Boolean(ticketId && state && typeof state === "object" && !Array.isArray(state)
    && "ticketSubmittedId" in state && state.ticketSubmittedId === ticketId);
}

// Mount only after the ticket detail has passed the customer's access check.
// Consume the navigation marker, but keep the notice visible for this visit:
// dismiss/back/reload must not turn an old ticket into a new confirmation.
export function TicketSubmittedNotice({ ticket }: { ticket: Pick<Ticket, "id" | "mailbox"> }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(() => isTicketSubmissionNotice(location.state, ticket.id));
  useEffect(() => {
    if (!isTicketSubmissionNotice(location.state, ticket.id)) return;
    const state = { ...location.state };
    delete state.ticketSubmittedId;
    navigate(location.pathname + location.search + location.hash, {
      replace: true, state: Object.keys(state).length ? state : null,
    });
  }, [ticket.id, location.pathname, location.search, location.hash, location.state, navigate]);

  if (!visible) return null;
  const fromAddress = ticket.mailbox?.address?.trim();
  return (
    <section className="ticket-submitted-notice" role="status" aria-live="polite">
      <CheckCircle2 className="ticket-submitted-notice__icon" size={22} aria-hidden="true" />
      <div>
        <h2>Your request has been received</h2>
        <p>{fromAddress ? <>Look out for emails from <strong className="ticket-submitted-notice__address">{fromAddress}</strong>.</>
          : <>Look out for email updates from our support team.</>} If you don’t see them in your inbox, check your <strong>Spam or Junk</strong> folder and mark our messages there as <strong>“Not spam.”</strong></p>
        <p>You can also view updates and reply directly on this ticket page.</p>
      </div>
      <button className="ticket-submitted-notice__close" type="button" aria-label="Dismiss ticket confirmation" onClick={() => setVisible(false)}>
        <X size={18} aria-hidden="true" />
      </button>
    </section>
  );
}
